import type { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import type { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import * as d3 from 'd3';

import { calculateLocalizer } from '@/src/components/vtkTwoView-utils-localizer';

// Half-length (world/mm) of the range crossbars, plus default styling.
export const LAX_RANGE_CROSS_WORLD = 50;
export const RANGE_CROSS_STROKE = 3;
export const RANGE_CROSS_OPACITY = 0.5;

export interface LocalizerDir2D {
  x: number;
  y: number;
  spacing: number; // mm/px of the destination image
}

export interface Linear2D {
  a: number;
  b: number;
  c: number;
  d: number;
}

type ViewportStateStore = ReturnType<typeof useViewportStateStore>;
type CurrentStudyStore = ReturnType<typeof useCurrentStudyStore>;

// d3 target can be an <svg> root or a <g> group; keep it permissive.
type D3Target = d3.Selection<any, unknown, null, undefined>;
type Point2D = [number, number];

/**
 * 2D unit direction of the localizer intersection line on `curImageUid`,
 * or null if there is no active reference / the planes don't intersect.
 */
export function getLocalizerDir2D(
  viewId: string,
  curImageUid: string,
  viewportStateStore: ViewportStateStore,
  currentStudyStore: CurrentStudyStore,
): LocalizerDir2D | null {
  const refUid
    = viewportStateStore.getActiveReferenceImageUid(viewId)
      ?? viewportStateStore.getReferenceImageUids(viewId)?.[0];
  if (!refUid) return null;

  const dst = currentStudyStore.$state.imageInfoDicom[curImageUid];
  const src = currentStudyStore.$state.imageInfoDicom[refUid];
  if (!dst || !src) return null;

  const { intersectionLinePoints } = calculateLocalizer(
    dst.ImageOrientationPatient,
    dst.ImagePositionPatient,
    dst.PixelSpacing[0],
    dst.PixelSpacing[1],
    dst.Rows,
    dst.Columns,
    src.ImageOrientationPatient,
    src.ImagePositionPatient,
    src.PixelSpacing[0],
    src.PixelSpacing[1],
    src.Rows,
    src.Columns,
  );
  if (!intersectionLinePoints) return null;

  const [a, b] = intersectionLinePoints;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;

  return { x: dx / len, y: dy / len, spacing: dst.PixelSpacing[0] };
}

/** Crossbar half-length in pixels for the current image spacing. */
export function rangeCrossHalfLen(dir: LocalizerDir2D): number {
  return dir.spacing > 0 ? LAX_RANGE_CROSS_WORLD / dir.spacing : LAX_RANGE_CROSS_WORLD;
}

interface CrossbarStyle {
  color: string;
  opacity?: number;
  strokeWidth?: number;
  /** Display overlay uses non-scaling-stroke; adjust overlay does not. */
  nonScalingStroke?: boolean;
}

function appendDashedCrossbar(
  target: D3Target,
  tip: Point2D,
  dir: LocalizerDir2D,
  halfLen: number,
  style: CrossbarStyle,
  linear?: Linear2D,
): void {
  // Offset from the tip along the localizer, in image-pixel units.
  const ox = dir.x * halfLen;
  const oy = dir.y * halfLen;
  // Map the offset into the drawing space. `linear` (image->screen 2x2) carries
  // rotation + scale for screen-space overlays (adjust). When omitted, the offset
  // stays in image px (display draws under a group matrix that does this instead).
  const dx = linear ? linear.a * ox + linear.c * oy : ox;
  const dy = linear ? linear.b * ox + linear.d * oy : oy;

  const a: Point2D = [tip[0] - dx, tip[1] - dy];
  const b: Point2D = [tip[0] + dx, tip[1] + dy];
  const line = d3.line<Point2D>().x(d => d[0]).y(d => d[1]).curve(d3.curveLinear);

  const path = target
    .append('path')
    .datum([a, b])
    .attr('fill', 'none')
    .attr('opacity', style.opacity ?? RANGE_CROSS_OPACITY)
    .attr('stroke', style.color)
    .attr('stroke-width', style.strokeWidth ?? RANGE_CROSS_STROKE)
    .attr('stroke-dasharray', '4 3')
    .attr('d', line);

  if (style.nonScalingStroke) path.attr('vector-effect', 'non-scaling-stroke');
}

export function drawRangeCrossbars(
  target: D3Target,
  pts: Point2D[],
  dir: LocalizerDir2D | null,
  style: CrossbarStyle,
  linear?: Linear2D, // image->screen linear part; omit for image-space overlays
): void {
  if (!dir || pts.length < 2) return;
  const halfLen = rangeCrossHalfLen(dir);
  appendDashedCrossbar(target, pts[0], dir, halfLen, style, linear);
  appendDashedCrossbar(target, pts[pts.length - 1], dir, halfLen, style, linear);
}
